// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { tagsApi } from '../api/tagsApi';
import type { CreateTagRequest, TagDTO } from '../types/tag';

const FEATURE_DISABLED_STATUS = 404;

interface TagsContextValue {
  /** Non-archived + archived tags in the brokerage (single cached fetch per session). */
  tags: TagDTO[];
  loading: boolean;
  error: string | null;
  /**
   * False once the initial fetch resolves as a 404 — the resource-server
   * 404s every /api/tags/** endpoint when the `tags` feature flag is off
   * for the tenant (Tags PRD §8). Consumers should hide ALL tag UI
   * (chips, pickers, filters, the Settings->Tags nav entry) when this is
   * false, per the "probe once, cache, hide-on-404" plan (§6.3).
   */
  featureEnabled: boolean;
  /** Refetch the full tag list (e.g. after create/update/merge/delete). */
  refetch: () => Promise<void>;
  /** Create a tag and merge it into the cached list; returns the created tag. */
  createTag: (data: CreateTagRequest) => Promise<TagDTO>;
  /** Resolve a tag id against the cached list; undefined when unresolvable (e.g. deleted mid fund-tag-propagation). */
  getTagById: (id: string) => TagDTO | undefined;
}

const TagsContext = createContext<TagsContextValue | null>(null);

export function TagsProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [tags, setTags] = useState<TagDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [featureEnabled, setFeatureEnabled] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tagsApi.list({ includeArchived: true });
      setTags(data);
      setFeatureEnabled(true);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === FEATURE_DISABLED_STATUS) {
        setFeatureEnabled(false);
        setTags([]);
      } else {
        console.error('Failed to load tags:', err);
        setError(err instanceof Error ? err.message : 'Failed to load tags');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createTag = useCallback(async (data: CreateTagRequest): Promise<TagDTO> => {
    const created = await tagsApi.create(data);
    setTags((prev) => [...prev, created]);
    return created;
  }, []);

  const getTagById = useCallback(
    (id: string) => tags.find((t) => t.id === id),
    [tags],
  );

  const value = useMemo<TagsContextValue>(() => ({
    tags,
    loading,
    error,
    featureEnabled,
    refetch: load,
    createTag,
    getTagById,
  }), [tags, loading, error, featureEnabled, load, createTag, getTagById]);

  return <TagsContext.Provider value={value}>{children}</TagsContext.Provider>;
}

export function useTags(): TagsContextValue {
  const context = useContext(TagsContext);
  if (!context) {
    throw new Error('useTags must be used within a TagsProvider');
  }
  return context;
}
